<script setup lang="ts">
import { getTranslatedProperty } from '@shopware-pwa/helpers-next';
import CheckoutSummaryValues from './CheckoutSummaryValues.vue';

withDefaults(
    defineProps<{
        reducedDisplay?: boolean;
    }>(),
    {
        reducedDisplay: false,
    },
);
const { totalPrice, cart } = useCart();
const { getShippingMethodCost, groupedCartItems, vendorTotals } = useGroupedCartItems();
const { t } = useI18n();

const calculatedTaxes = computed(() => {
    const calculatedTaxes = cart.value?.price?.calculatedTaxes;
    return calculatedTaxes || [];
});

const calculatedDiscount = computed(() => {
    return cart.value.lineItems
        .filter(lineItem => lineItem.type === 'promotion')
        .map(promotion => promotion.price.totalPrice)
        .reduce((acc, cur) => acc + cur, 0);
});

const vendorTotalWithShippingCost = computed(() => {
    return (vendorTotals: string, vendorId: string) => {
        const shipping = getShippingMethodCost(vendorId);
        return vendorTotals + shipping.totalPrice;
    };
});

const taxRate = cart.value?.price?.calculatedTaxes?.[0]?.taxRate || 0;
const calculateVat = (price: number) => {
    if (cart.value?.price?.taxStatus === 'gross') {
        return price - price / (1 + taxRate / 100);
    } else {
        return price * (taxRate / 100);
    }
};
</script>

<template>
    <div class="bg-neutral-300 p-4">
        <h2 class="pb-2 text-xl font-extrabold">{{ $t('checkout.summary.heading') }}</h2>

        <div
            v-for="(group, index) of groupedCartItems"
            :key="group?.vendor?.id ?? index"
            v-auto-animate
            class="mb-2 border-b border-neutral-500 pb-2"
        >
            <span class="text-sm font-bold">{{ getTranslatedProperty(group.vendor, 'name') }}</span>
            <template v-if="cart.price.taxStatus === 'net'">
                <CheckoutSummaryValues
                    :label="t('checkout.summary.totalGoods')"
                    :value="vendorTotals[group.vendor?.id]"
                />
                <CheckoutSummaryValues
                    :label="t('checkout.cart.shippingCosts')"
                    :value="getShippingMethodCost(group.vendor?.id).totalPrice"
                />
                <CheckoutSummaryValues
                    :label="t('checkout.summary.taxLabel')"
                    :value="calculateVat(vendorTotalWithShippingCost(vendorTotals[group.vendor?.id], group.vendor?.id))"
                />
                <CheckoutSummaryValues
                    :label="t('checkout.summary.subTotalGroup')"
                    :value="
                        vendorTotalWithShippingCost(vendorTotals[group.vendor?.id], group.vendor?.id) +
                        calculateVat(vendorTotalWithShippingCost(vendorTotals[group.vendor?.id], group.vendor?.id))
                    "
                    custom-css="font-semibold"
                />
            </template>
            <template v-if="cart.price.taxStatus === 'gross'">
                <CheckoutSummaryValues
                    :label="t('checkout.summary.totalGoods')"
                    :value="vendorTotals[group.vendor?.id]"
                />
                <CheckoutSummaryValues
                    :label="t('checkout.cart.shippingCosts')"
                    :value="getShippingMethodCost(group.vendor?.id).totalPrice"
                />
                <CheckoutSummaryValues
                    :label="t('checkout.summary.subTotalGroup')"
                    :value="vendorTotalWithShippingCost(vendorTotals[group.vendor?.id], group.vendor?.id)"
                    custom-css="font-semibold"
                />
                <CheckoutSummaryValues
                    :label="t('checkout.summary.vatIncluded')"
                    :value="calculateVat(vendorTotalWithShippingCost(vendorTotals[group.vendor?.id], group.vendor?.id))"
                />
            </template>
        </div>

        <!-- CUSTOM IDS-168: Extracted summary total to use component for checkout page as well-->
        <CheckoutSummaryTotal
            :tax-status="cart.price.taxStatus"
            :total-price="totalPrice"
            :net-price="cart.price.netPrice"
            :calculated-discount="calculatedDiscount"
            :calculated-taxes="calculatedTaxes"
        />
    </div>
    <!--
    <CheckoutTotalDeliveryTime :cart="cart" />
--></template>
